import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import BaseDialog from '../BaseDialog';
import useApiControl from '../../../common/useApiControl';

const DisputeDialog = ({ isVisible, onClose, review, onSubmitted }) => {
  const [claimedResult, setClaimedResult] = useState('');
  const isInvalidationRequest = Number(review?.result_revision_count ?? 0) > 0;

  useEffect(() => {
    setClaimedResult('');
  }, [isVisible, review?.round_id]);

  const params = useMemo(() => ({
    event_id: review?.event_id,
    round_id: review?.round_id,
    claimed_result: claimedResult,
  }), [review?.event_id, review?.round_id, claimedResult]);

  const { executeRequest } = useApiControl({
    action: '/dispute_round_result',
    params,
    method: 'POST',
  });
  const { executeRequest: requestInvalidation } = useApiControl({
    action: '/request_round_invalidation',
    params: {
      event_id: review?.event_id,
      round_id: review?.round_id,
    },
    method: 'POST',
  });

  const handleSubmit = async () => {
    if (isInvalidationRequest) {
      await requestInvalidation();
    } else {
      await executeRequest();
    }
    await onSubmitted();
    setClaimedResult('');
    onClose();
  };

  const answers = review?.answers ?? [];
  const ownerResult = review?.result !== null && review?.result !== undefined
    ? String(review.result)
    : '';
  const remaining = Math.max(0, Number(review?.result_dispute_remaining ?? 0));
  const canSubmit = isInvalidationRequest || (claimedResult !== '' && remaining > 0);

  return (
    <BaseDialog isVisible={isVisible} onClose={onClose}>
      <Box sx={{ width: 380, maxWidth: '100%', p: 1 }}>
        <Typography variant="h6" sx={{ fontSize: 17, mb: 1 }}>
          {isInvalidationRequest ? 'ラウンドの無効を希望します' : 'リクエストを送信します'}
        </Typography>
        {isInvalidationRequest ? (
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>
            訂正後の結果でもラウンドを成立させることが難しい場合に送信してください。
            一定数に達すると、このラウンドは不成立となりベットが返却されます。
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>
              判定の再確認をリクエストできます。
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>
              オーナーの選択結果が事実と異なる場合だけ、正しいと思う選択肢を選んでください。
              問題がなければ結果の確定までお待ちください。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              このルームでリクエスト可能な回数: {remaining} 回
            </Typography>
            <FormControl component="fieldset">
              <FormLabel component="legend">正しいと思う選択肢</FormLabel>
              <RadioGroup value={claimedResult} onChange={(event) => setClaimedResult(event.target.value)}>
                {answers.map((answer, index) => {
                  const value = String(index);
                  return (
                    <FormControlLabel
                      key={`${answer}-${index}`}
                      value={value}
                      control={<Radio />}
                      label={answer}
                      disabled={value === ownerResult}
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          </>
        )}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="outlined" onClick={onClose}>
            キャンセル
          </Button>
          <Button variant="contained" color="warning" onClick={handleSubmit} disabled={!canSubmit}>
            {isInvalidationRequest ? 'ラウンドの無効を希望する' : 'リクエストする'}
          </Button>
        </Box>
      </Box>
    </BaseDialog>
  );
};

export default DisputeDialog;
